import { create } from 'zustand';
import { CourseProps } from '@/features/dashboard/domain/entities/Course';

/**
 * Course DTO for store
 * Aligned with CourseProps from domain entity
 */
export interface CourseDTO extends CourseProps {
  // Additional computed fields can be added here if needed
  enrollmentCount?: number;
  status?: 'active' | 'completed' | 'upcoming';
}

/**
 * Courses Store State Interface
 * Manages global state for courses
 */
interface CoursesState {
  // Data
  courses: CourseDTO[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions - Setters
  setCourses: (courses: CourseDTO[]) => void;
  addCourse: (course: CourseDTO) => void;
  updateCourse: (id: string, course: Partial<CourseDTO>) => void;
  deleteCourse: (id: string) => void;

  // Actions - State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Helpers
  getCourseById: (id: string) => CourseDTO | undefined;
  getCoursesByInstructor: (instructor: string) => CourseDTO[];
  getActiveCourses: () => CourseDTO[];
  getCompletedCourses: () => CourseDTO[];
  getInProgressCourses: () => CourseDTO[];
  clearCourses: () => void;
  reset: () => void;
}

/**
 * Initial state for courses store
 */
const initialState = {
  courses: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Courses Store
 *
 * Global state management for courses using Zustand.
 * Features:
 * - In-memory storage only (NO persistence)
 * - CRUD operations
 * - Loading and error states
 * - Helper selectors for filtering
 *
 * IMPORTANT: Courses are dynamic data that should always be fresh.
 * No localStorage caching to ensure data consistency.
 *
 * Usage:
 * ```typescript
 * const { courses, isLoading, setCourses } = useCoursesStore();
 * ```
 *
 * Note: This store should be populated by use cases, not repositories directly.
 * Follow hexagonal architecture principles.
 */
export const useCoursesStore = create<CoursesState>()((set, get) => ({
  ...initialState,

  // Setters
  setCourses: (courses) =>
    set({
      courses,
      lastFetched: Date.now(),
      error: null,
    }),

  addCourse: (course) =>
    set((state) => ({
      courses: [...state.courses, course],
      lastFetched: Date.now(),
    })),

  updateCourse: (id, updatedCourse) =>
    set((state) => ({
      courses: state.courses.map((course) =>
        course.id === id ? { ...course, ...updatedCourse } : course
      ),
      lastFetched: Date.now(),
    })),

  deleteCourse: (id) =>
    set((state) => ({
      courses: state.courses.filter((course) => course.id !== id),
      lastFetched: Date.now(),
    })),

  // State Management
  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error, isLoading: false }),

  // Helpers
  getCourseById: (id) => {
    const state = get();
    return state.courses.find((course) => course.id === id);
  },

  getCoursesByInstructor: (instructor) => {
    const state = get();
    return state.courses.filter(
      (course) =>
        course.instructor.toLowerCase().includes(instructor.toLowerCase())
    );
  },

  getActiveCourses: () => {
    const state = get();
    return state.courses.filter(
      (course) => course.status === 'active' || course.progress < 100
    );
  },

  getCompletedCourses: () => {
    const state = get();
    return state.courses.filter(
      (course) => course.status === 'completed' || course.progress === 100
    );
  },

  getInProgressCourses: () => {
    const state = get();
    return state.courses.filter(
      (course) => course.progress > 0 && course.progress < 100
    );
  },

  clearCourses: () =>
    set({
      courses: [],
      lastFetched: null,
    }),

  reset: () => set(initialState),
}));

/**
 * Selectors for optimized component rendering
 * Use these to select specific parts of the state
 */
export const coursesSelectors = {
  // Select all courses
  selectCourses: (state: CoursesState) => state.courses,

  // Select loading state
  selectIsLoading: (state: CoursesState) => state.isLoading,

  // Select error state
  selectError: (state: CoursesState) => state.error,

  // Select course by ID
  selectCourseById: (id: string) => (state: CoursesState) =>
    state.courses.find((course) => course.id === id),

  // Select courses by instructor
  selectCoursesByInstructor: (instructor: string) => (state: CoursesState) =>
    state.courses.filter((course) =>
      course.instructor.toLowerCase().includes(instructor.toLowerCase())
    ),

  // Select active courses
  selectActiveCourses: (state: CoursesState) =>
    state.courses.filter(
      (course) => course.status === 'active' || course.progress < 100
    ),

  // Select completed courses
  selectCompletedCourses: (state: CoursesState) =>
    state.courses.filter(
      (course) => course.status === 'completed' || course.progress === 100
    ),

  // Select in-progress courses
  selectInProgressCourses: (state: CoursesState) =>
    state.courses.filter(
      (course) => course.progress > 0 && course.progress < 100
    ),

  // Select courses with high progress (>75%)
  selectHighProgressCourses: (state: CoursesState) =>
    state.courses.filter((course) => course.progress >= 75),

  // Select courses with low progress (<25%)
  selectLowProgressCourses: (state: CoursesState) =>
    state.courses.filter((course) => course.progress < 25),

  // Select courses count
  selectCoursesCount: (state: CoursesState) => state.courses.length,

  // Check if data is stale (older than 5 minutes)
  selectIsStale: (state: CoursesState) => {
    if (!state.lastFetched) return true;
    const fiveMinutes = 5 * 60 * 1000;
    return Date.now() - state.lastFetched > fiveMinutes;
  },

  // Select average progress
  selectAverageProgress: (state: CoursesState) => {
    if (state.courses.length === 0) return 0;
    const total = state.courses.reduce(
      (sum, course) => sum + course.progress,
      0
    );
    return Math.round(total / state.courses.length);
  },
};
